import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ProductAd from '../components/ProductAd';
import PanditLogo from '../components/PanditLogo';
import QuotaModal from '../components/QuotaModal';
import MessageActions from '../components/MessageActions';
import AiContentNotice from '../components/AiContentNotice';
import { deepaiChat } from '../lib/api';
import { bidturnsAnalyze, newSessionId } from '../lib/bidturns';
import { loadChat, saveChat } from '../lib/chats';
import { useAuth } from '../contexts/AuthContext';
import { useUsage } from '../contexts/UsageContext';
import { ACTIVE_ASSISTANT_KEY, ASSISTANTS } from './AssistantsPage';
import { PERSONAL_CONTEXT_KEY } from '../components/Sidebar';
import './ChatPage.css';

// Build the "Personal context" preamble from Settings → Personal
// context. Empty string means the user hasn't set anything, in
// which case we add nothing to the system prompt (lets us join with
// the assistant prompt cleanly without an awkward blank line).
function buildPersonalPreamble() {
  let pc = null;
  try {
    const raw = localStorage.getItem(PERSONAL_CONTEXT_KEY);
    pc = raw ? JSON.parse(raw) : null;
  } catch { return ''; }
  if (!pc) return '';
  const lines = [];
  if (pc.name) lines.push(`The user prefers to be called "${pc.name}".`);
  if (pc.about) lines.push(`About them: ${pc.about}`);
  if (pc.tone && pc.tone !== 'balanced') {
    lines.push(pc.tone === 'concise'
      ? 'They prefer short, direct answers — skip preamble, get to the point.'
      : 'They prefer thorough, detailed answers with context and examples.');
  }
  return lines.length ? `User context: ${lines.join(' ')}` : '';
}

function loadActiveAssistant() {
  try {
    const raw = localStorage.getItem(ACTIVE_ASSISTANT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Old v1 entries don't have systemPrompt — treat them as "no persona".
    if (!parsed?.systemPrompt) return null;
    return parsed;
  } catch { return null; }
}

// Sync the active-assistant localStorage entry to the chat the user
// just opened:
//   - assistant chat → restore that assistant (so follow-up messages
//     keep the persona)
//   - regular chat → clear any active assistant (so opening a normal
//     chat while a persona was lingering doesn't bleed Chanakya/etc.
//     into the next reply)
// Dispatches the same custom event AssistantsPage.handleStart uses
// so ChatPage's useEffect picks the change up in the same tab.
function syncAssistantToChat(chat) {
  if (!chat) return;
  try {
    if (chat.scope === 'assistant' && chat.assistantId) {
      const def = ASSISTANTS.find((a) => a.id === chat.assistantId);
      if (!def) return;   // assistant was deleted from the codebase
      localStorage.setItem(ACTIVE_ASSISTANT_KEY, JSON.stringify({
        id: def.id,
        name: def.name,
        tagline: def.tagline,
        emoji: def.emoji,
        systemPrompt: def.systemPrompt,
      }));
    } else {
      // Regular chat — drop the persona.
      localStorage.removeItem(ACTIVE_ASSISTANT_KEY);
    }
    window.dispatchEvent(new Event('arthum:assistant-changed'));
  } catch { /* SSR / disabled storage */ }
}

const BUILD_TAG = 'b24-arthum-mesh';
const WORD_DELAY_MS = 22;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// localStorage backup of the in-progress chat so it survives anything
// that resets ChatPage state — tab switches, hard refreshes, accidental
// route changes. This is independent of backend chat persistence (which
// only kicks in for logged-in users + has a 700ms debounce that can be
// lost mid-flight). 7-day TTL keeps stale anon sessions from suddenly
// resurrecting weeks later.
const DRAFT_KEY = 'arthum.chatDraft.v1';
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function loadChatDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.messages?.length) return null;
    if (parsed.updatedAt && Date.now() - parsed.updatedAt > DRAFT_TTL_MS) return null;
    return parsed;
  } catch { return null; }
}
function saveChatDraft(messages, chatId) {
  try {
    if (!messages?.length) {
      localStorage.removeItem(DRAFT_KEY);
      return;
    }
    // Strip live-only fields (streaming flag, internal ids would
    // collide on rehydrate). Keep role, text, ad, intent — that's
    // everything the UI needs to render the bubble again.
    const lean = messages.map((m) => ({
      role: m.role,
      text: m.text,
      ...(m.ad ? { ad: m.ad } : null),
      ...(m.intent ? { intent: m.intent } : null),
      ...(m.from ? { from: m.from } : null),
    }));
    localStorage.setItem(DRAFT_KEY, JSON.stringify({
      messages: lean,
      chatId: chatId || null,
      updatedAt: Date.now(),
    }));
  } catch { /* quota / disabled storage — silent */ }
}

// One backend: Bedrock DeepSeek-R1. Kept as an array of one for the
// label lookup below; if/when we add real model alternatives, push to
// this list and the UI picker comes back.
const MODEL_OPTIONS = [
  { value: 'deepseek-r1', label: 'DeepSeek R1' },
];

const SendArrow = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="#fff" stroke="none">
    <path d="M13 2L4.5 13.5H11L10 22l8.5-11.5H13z" />
  </svg>
);

// Rotating waiting messages — a single static "Sutrum is thinking" stops
// reassuring the user past ~3s on a 5–7s wait. Cycling phrases makes the
// delay feel like active work. Each phrase displays for ~2.2s before the
// next one cross-fades in via the `key` change.
const CHAT_TYPING_PHRASES = [
  'Sutrum is thinking',
  'Gathering thoughts',
  'Pulling the threads together',
  'Almost there',
  'Choosing the right words',
];

function ChatTypingIndicator() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % CHAT_TYPING_PHRASES.length);
    }, 2200);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="mrow mrow-ai">
      {/* bub-ai-thinking opts out of the full-width treatment .bub-ai now
          has — the typing indicator should sit as a small chip on the
          left, not span the message column. */}
      <div className="bub-ai bub-ai-thinking">
        <div className="typing-row">
          <div className="typing"><span /><span /><span /></div>
          <span key={idx} className="typing-text typing-text-fade">
            {CHAT_TYPING_PHRASES[idx]}
          </span>
        </div>
      </div>
    </div>
  );
}

function ChatPage({ active = true, activeChatId, onChatPersisted, mintChatId, onRequestLogin, onSubscribe }) {
  const { user } = useAuth();
  const { bump: bumpUsage } = useUsage();
  // Hydrate messages from the localStorage draft on first mount. If
  // there's an explicit activeChatId from the sidebar the load effect
  // below will overwrite this with the backend copy — but for the
  // common "I just typed something then switched tabs / refreshed"
  // case, this puts the user back exactly where they were instantly.
  const [messages, setMessages] = useState(() => {
    if (activeChatId) return [];
    const draft = loadChatDraft();
    if (draft && (!draft.chatId || draft.chatId === activeChatId)) {
      return draft.messages.map((m, i) => ({ ...m, id: `d-${i}` }));
    }
    return [];
  });
  const [chatId, setChatId] = useState(() => {
    if (activeChatId) return activeChatId;
    return loadChatDraft()?.chatId || null;
  });
  const [draft, setDraft] = useState('');
  // Hard-coded since the picker UI was removed (only one model in play).
  // Kept in scope so the AI message bubble can label its source.
  const model = 'deepseek-r1';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Server-side limit state. When a 429 lands we keep the usage
  // snapshot + the kind ('DAILY_TOKEN_LIMIT' / 'MONTHLY_TOKEN_LIMIT')
  // so QuotaModal renders the right copy. blocked stays true until
  // the relevant reset has passed (daily resets in hours; monthly
  // could be days).
  const [quotaUsage, setQuotaUsage] = useState(null);
  const [quotaKind, setQuotaKind] = useState(null);
  const [quotaBlocked, setQuotaBlocked] = useState(false);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const scrollRef = useRef(null);
  const sessionIdRef = useRef(null);
  const saveTimerRef = useRef(null);
  if (!sessionIdRef.current) sessionIdRef.current = newSessionId();

  // Wipe any free-tier counter left over from the old hard-gate
  // implementation. Anonymous users now have unlimited local chat.
  useEffect(() => {
    try { localStorage.removeItem('arthum.freeChatCount.v1'); } catch {}
  }, []);

  // Persona steering. Read from localStorage on mount; AssistantsPage
  // writes it when the user clicks "Start chatting" then navigates here.
  // Re-read on tab focus too so two open tabs stay in sync.
  const [assistant, setAssistant] = useState(loadActiveAssistant);
  useEffect(() => {
    const reload = () => setAssistant(loadActiveAssistant());
    window.addEventListener('focus', reload);
    // 'storage' only fires for OTHER tabs — same-tab setItem doesn't
    // trigger it. We dispatch 'arthum:assistant-changed' from
    // AssistantsPage.handleStart for the same-tab case so the very
    // next message gets correctly tagged scope='assistant'. Without
    // this listener the chat would land in the wrong sidebar.
    window.addEventListener('storage', reload);
    window.addEventListener('arthum:assistant-changed', reload);
    return () => {
      window.removeEventListener('focus', reload);
      window.removeEventListener('storage', reload);
      window.removeEventListener('arthum:assistant-changed', reload);
    };
  }, []);
  const clearAssistant = () => {
    try { localStorage.removeItem(ACTIVE_ASSISTANT_KEY); } catch {}
    setAssistant(null);
    // Same-tab notification so future logic listening for the change
    // (e.g. a future sidebar badge) updates immediately too.
    try { window.dispatchEvent(new Event('arthum:assistant-changed')); } catch {}
  };

  // Load chat when sidebar selection changes.
  //
  // The lastSeenChatIdRef sentinel skips the auto-clear branch on the
  // very first run of this effect — otherwise the useState hydration
  // we just did from localStorage would be immediately wiped because
  // activeChatId is null on initial mount of a fresh visit. Subsequent
  // changes (sidebar pick → activeChatId becomes a string, "+ New chat"
  // → activeChatId becomes null) still behave correctly.
  const lastSeenChatIdRef = useRef(undefined);
  useEffect(() => {
    const firstRun = lastSeenChatIdRef.current === undefined;
    lastSeenChatIdRef.current = activeChatId;
    let alive = true;

    if (firstRun) {
      // On boot: if the URL/sidebar gave us a chatId, fetch its messages
      // (overrides any localStorage draft for that same id). If not, the
      // draft stays in place and we do nothing.
      if (!activeChatId) return undefined;
      setChatId(activeChatId);
      loadChat(activeChatId)
        .then((chat) => {
          if (!alive || !chat) return;
          setMessages(
            (chat.messages || []).map((m, i) => ({ ...m, id: `${activeChatId}-${i}` }))
          );
          // If this chat was started under a persona, rehydrate it
          // before the user types anything — otherwise the first
          // follow-up message would lose the assistant context.
          syncAssistantToChat(chat);
        })
        .catch((err) => alive && setError(err.message || 'Could not load chat.'));
      return () => { alive = false; };
    }

    // Subsequent runs: user explicitly picked a new chat or clicked
    // "+ New chat". Clearing messages here is the intended UX.
    if (!activeChatId) {
      setChatId(null);
      setMessages([]);
      setError('');
      return undefined;
    }
    setChatId(activeChatId);
    loadChat(activeChatId)
      .then((chat) => {
        if (!alive || !chat) return;
        setMessages(
          (chat.messages || []).map((m, i) => ({ ...m, id: `${activeChatId}-${i}` }))
        );
        syncAssistantToChat(chat);
      })
      .catch((err) => alive && setError(err.message || 'Could not load chat.'));
    return () => { alive = false; };
  }, [activeChatId]);

  // Persist the in-memory chat to localStorage on every change so we
  // can rehydrate after a tab switch / refresh. Backend persistence
  // (for logged-in users) is the source of truth for *saved* chats —
  // this layer is the safety net for the in-progress one.
  useEffect(() => {
    saveChatDraft(messages, chatId);
  }, [messages, chatId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading]);

  // Persist immediately — no debounce. Called twice per turn now:
  //   1. Right after the user message is appended (mints chatId on
  //      first save, so the sidebar row appears INSTANTLY).
  //   2. Right after the AI reply finishes streaming.
  // The second call is intentionally not debounced either — when the
  // turn completes we want the row's title + updatedAt fresh on the
  // sidebar before the user has a chance to switch tabs.
  const persistNow = useCallback(async (msgList, currentChatId, extras = {}) => {
    // Belt-and-suspenders guard: callers that capture msgList inside a
    // deferred setState updater could pass undefined. Treat it as a
    // no-op rather than crashing the send flow.
    if (!Array.isArray(msgList) || msgList.length === 0) return null;
    // Both anon (localStorage) and signed-in (DDB) flow through saveChat;
    // the dispatcher in lib/chats.js routes by auth state.
    const idToUse = currentChatId || mintChatId();
    const isNew = !currentChatId;
    try {
      const saved = await saveChat(idToUse, {
        messages: msgList.map(({ id, streaming, ...rest }) => rest),
        ...extras,   // scope / assistantId / assistantName on first save
      });
      if (isNew) setChatId(saved.chatId);
      onChatPersisted?.(saved);
      return saved;
    } catch (err) {
      console.warn('[chat save]', err.message);
      return null;
    }
  }, [mintChatId, onChatPersisted]);

  const handleSend = async (text) => {
    const question = (text ?? draft).trim();
    if (!question || loading) return;

    // Quota gate. After a 429, keep the modal-armed state until the
    // matching reset has passed (monthly resets are days out; daily
    // hours). Re-popping the modal is the clearest signal nothing has
    // changed yet.
    const relevantReset = quotaKind?.startsWith('MONTHLY')
      ? quotaUsage?.monthlyResetMs
      : quotaUsage?.dailyResetMs;
    if (quotaBlocked && relevantReset && Date.now() < relevantReset) {
      setShowQuotaModal(true);
      return;
    }
    if (quotaBlocked) setQuotaBlocked(false);

    setDraft('');

    const userMsg = { id: `u-${Date.now()}`, role: 'user', text: question };
    const history = messages.map((m) => ({
      role: m.role === 'user' ? 'user' : 'assistant',
      content: m.text,
    }));
    const afterUser = [...messages, userMsg];
    setMessages(afterUser);
    setLoading(true);
    setError('');

    // ── INSTANT SIDEBAR REGISTRATION ──
    // Persist the user message NOW so the sidebar row appears
    // immediately and the chat is recoverable even if the AI call
    // fails. First-save also stamps scope/assistant fields.
    const extras = assistant
      ? { scope: 'assistant', assistantId: assistant.id, assistantName: assistant.name }
      : { scope: 'chat' };
    // Fire concurrently with the LLM call below — we await the result
    // before the second save so we know which chatId to update.
    const firstSavePromise = persistNow(afterUser, chatId, extras);

    const [llmRes, adRes] = await Promise.allSettled([
      deepaiChat({
        question,
        conversationHistory: history,
        // Combined system prompt = Personal context (Settings →
        // Personal context, opt-in per-user blurb) + the assistant
        // persona the user picked on /assistants. Either or both may
        // be empty; we filter blanks before joining.
        systemPrompt: [buildPersonalPreamble(), assistant?.systemPrompt]
          .filter(Boolean).join('\n\n') || null,
      }),
      bidturnsAnalyze({ query: question, sessionId: sessionIdRef.current }),
    ]);

    if (llmRes.status !== 'fulfilled') {
      const reason = llmRes.reason;
      // 429 = server-side quota exhausted. Take the user's last bubble
      // back out (their question never got answered), update the local
      // quota snapshot, and pop the dedicated modal instead of the
      // generic error toast.
      if (reason?.status === 429 || reason?.code === 'QUOTA_EXCEEDED') {
        setMessages((prev) => prev.filter((m) => m.id !== userMsg.id));
        setDraft(question);   // give them their text back
        if (reason.usage) {
          setQuotaUsage(reason.usage);
          bumpUsage(reason.usage);
        }
        if (reason.kind) setQuotaKind(reason.kind);
        setQuotaBlocked(true);
        setShowQuotaModal(true);
        setLoading(false);
        return;
      }
      setError(reason?.message || 'Something went wrong.');
      setLoading(false);
      return;
    }

    // Successful response — feed the fresh usage snapshot into the
    // shared meter so it reflects this turn's token spend.
    if (llmRes.value.usage) bumpUsage(llmRes.value.usage);

    const adPayload = adRes.status === 'fulfilled' ? adRes.value : null;
    const finalText = llmRes.value.answer || '(no response)';
    const aiId = `a-${Date.now()}`;
    const modelLabel = MODEL_OPTIONS.find((m) => m.value === model)?.label || model;

    setMessages((prev) => [
      ...prev,
      { id: aiId, role: 'assistant', text: '', from: modelLabel, ad: null, intent: null, streaming: true },
    ]);
    setLoading(false);

    const tokens = finalText.split(/(\s+)/);
    let acc = '';
    for (let i = 0; i < tokens.length; i += 1) {
      acc += tokens[i];
      const snap = acc;
      setMessages((prev) => prev.map((m) => (m.id === aiId ? { ...m, text: snap } : m)));
      if (tokens[i].trim()) await sleep(WORD_DELAY_MS);
    }

    // Compute the final messages array synchronously rather than
    // capturing it inside a setMessages updater. React 18+ may defer
    // updaters (esp. for unmounted/hidden components), which means
    // a `let final; setMessages((prev) => { final = ...; })` pattern
    // can leave `final` undefined when read on the next line — that
    // was the root cause of the "Cannot read properties of undefined
    // (reading 'length')" crash in persistNow.
    const final = [
      ...afterUser,
      {
        id: aiId,
        role: 'assistant',
        text: finalText,
        from: modelLabel,
        ad: adPayload?.adData || null,
        intent: adPayload?.intent || null,
      },
    ];
    setMessages(final);

    // Second persist — same chat row, now with the AI reply included.
    // We need to wait for the first save to land so we know the chatId
    // it minted (state hasn't re-rendered yet, so the closure's chatId
    // is still null for a fresh chat). Awaiting briefly here is fine —
    // the LLM call already completed by the time we get here.
    const firstSaved = await firstSavePromise;
    const finalChatId = firstSaved?.chatId || chatId;
    persistNow(final, finalChatId, extras);
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const firstName = (user?.name || user?.email?.split('@')[0] || '').trim().split(/\s+/)[0];
  const hasMessages = messages.length > 0;

  const modelLabel = MODEL_OPTIONS.find((m) => m.value === model)?.label || model;

  return (
    <div className={`page ${active ? 'on' : ''}`} id="pg-chat">
      {assistant && (
        <AssistantPill assistant={assistant} onClear={clearAssistant} />
      )}
      {!hasMessages && (
        <div className="welcome">
          <div className="welcome-logo-wrap">
            <PanditLogo size="lg" className="welcome-logo pandit-logo-welcome" alt="Sutrum AI" />
          </div>
          <div className="welcome-h">
            {assistant
              ? `Chatting as ${assistant.name}. What can I help with?`
              : firstName
                ? `Welcome ${firstName}, what can I help with?`
                : 'What can I help with?'}
          </div>
          <div className="welcome-input-wrap">
            <div className="welcome-input-box">
              <textarea
                className="msg-ta"
                placeholder="Ask Sutrum AI"
                rows={1}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKey}
                style={{ flex: 1, height: 24, maxHeight: 80, padding: '4px 0', lineHeight: 1.5 }}
              />
              <button className="send-btn" onClick={() => handleSend()} disabled={loading || !draft.trim()}>
                <SendArrow />
              </button>
            </div>
            {!user && (
              <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--ink3)', marginTop: 12 }}>
                <SyncHint onSignIn={() => onRequestLogin?.()} />
              </div>
            )}
            {/* Welcome state also carries the Report affordance so the
                Play/App Store reviewer sees it on first open, before
                any AI content has been generated. */}
            <AiContentNotice kind="chat" meta={{ page: 'chat-welcome' }} />
          </div>
        </div>
      )}

      {hasMessages && (
        <div id="convo-screen" style={{ display: 'flex', flex: 1, flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
          <div className="chat-body" ref={scrollRef}>
            <div className="chat-msgs">
              {messages.map((m, i) => (
                <div key={m.id} className={`mrow ${m.role === 'user' ? 'mrow-u' : 'mrow-ai'}`}>
                  {/* Model-name label removed — users don't need to see
                      "DeepSeek R1" on every assistant turn. */}
                  <div className={m.role === 'user' ? 'bub-u' : 'bub-ai'}>
                    {m.role === 'assistant'
                      ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.text}</ReactMarkdown>
                      : m.text}
                  </div>
                  {m.role === 'assistant' && m.ad?.product_name && (
                    <div style={{ marginTop: 6 }}>
                      <ProductAd ad={m.ad} intent={m.intent} />
                    </div>
                  )}
                  {/* Action row — copy + like/dislike on assistant turns,
                      edit + copy on user turns. Hidden until hover. */}
                  {!m.streaming && (
                    <MessageActions
                      role={m.role}
                      text={m.text || ''}
                      onRegenerate={m.role === 'assistant' && i === messages.length - 1 && !loading
                        ? () => {
                            // Drop the last assistant turn, resend the last user prompt.
                            const lastUser = [...messages].reverse().find((x) => x.role === 'user');
                            if (!lastUser?.text) return;
                            setMessages((cur) => cur.filter((x) => x.id !== m.id));
                            handleSend(lastUser.text);
                          }
                        : undefined}
                      onEdit={m.role === 'user' && i === messages.length - 1 && !loading
                        ? () => setDraft(m.text || '')
                        : undefined}
                    />
                  )}
                </div>
              ))}
              {loading && <ChatTypingIndicator />}
              {error && (
                <div className="mrow mrow-ai">
                  <div className="bub-ai msg-error">{error}</div>
                </div>
              )}
            </div>
          </div>
          <div className="chat-foot">
            <div className="chat-foot-inner">
              <div className="input-box">
                <textarea
                  className="msg-ta"
                  placeholder="Ask Sutrum AI"
                  rows={1}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onKey}
                />
                <div className="input-row-inner">
                  <button className="send-btn" onClick={() => handleSend()} disabled={loading || !draft.trim()}>
                    <SendArrow />
                  </button>
                </div>
              </div>
              <div className="input-hint">
                Sutrum AI may make mistakes. Verify important information.
                {!user && (
                  <>
                    {' · '}
                    <SyncHint onSignIn={() => onRequestLogin?.()} />
                  </>
                )}
              </div>
              {/* Always-visible Report affordance for AI content policy
                  compliance. Per-message report already lives in
                  MessageActions; this is the top-level entry point. */}
              <AiContentNotice kind="chat" meta={{ page: 'chat' }} />
            </div>
          </div>
        </div>
      )}

      {showQuotaModal && (
        <QuotaModal
          kind={quotaKind}
          usage={quotaUsage}
          onClose={() => setShowQuotaModal(false)}
          onSubscribe={() => { setShowQuotaModal(false); onSubscribe?.(); }}
        />
      )}
    </div>
  );
}

// Soft nudge shown to anonymous users. No gate — just a reminder that
// their chats live only on this device until they sign in.
function SyncHint({ onSignIn }) {
  return (
    <span>
      Chats saved on this device ·{' '}
      <button
        type="button"
        onClick={onSignIn}
        style={{
          background: 'none', border: 'none', padding: 0,
          color: 'var(--accent)', cursor: 'pointer',
          font: 'inherit', textDecoration: 'underline',
        }}
      >
        Sign in to sync
      </button>
    </span>
  );
}

// Small floating pill above the chat that tells the user which assistant
// is currently steering the conversation. The × clears the assistant
// from localStorage and reverts to the default sutrumai.com prompt.
function AssistantPill({ assistant, onClear }) {
  return (
    <div className="assistant-pill">
      <span className="assistant-pill-emoji" aria-hidden>{assistant.emoji}</span>
      <span className="assistant-pill-text">
        Chatting as <strong>{assistant.name}</strong>
      </span>
      <button
        className="assistant-pill-x"
        onClick={onClear}
        aria-label={`Stop chatting as ${assistant.name}`}
        title="Use default sutrumai.com"
      >
        ×
      </button>
    </div>
  );
}

export default ChatPage;
